import { Mat } from './Mat';

export async function urlToImageData(url: string) {
    const blob = await fetch(url).then(r => r.blob());
    const imageBitmap = await createImageBitmap(blob);
    const imageData = imgToImageData(imageBitmap);
    imageBitmap.close();
    return imageData;
}

export async function imgToImageData(imgElement: any) {
    const canvas = getCanvasRead(imgElement.width, imgElement.height);
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    if (!ctx) {
        console.warn("Failed to create 2d context!");
        return null;
    }

    ctx.drawImage(imgElement, 0, 0, imgElement.width, imgElement.height)
    return ctx.getImageData(0, 0, canvas.width, canvas.height);
}

const getCanvasRead = (() => {
    // Offscreen canvas used to create encoded image from cv.Mat.
    // Reuse it if possible.
    let canvas: any = null;

    /**
     * @param {Number} width
     * @param {Number} height
     * @returns {OffscreenCanvas | HTMLCanvasElement}
     */
    return (width: number, height: number): any => {
        if (!canvas) {
            if(typeof OffscreenCanvas == 'undefined'){
                canvas = document.createElement("canvasRead");
                canvas.height = ''+height+'px';
                canvas.width = ''+width+'px';
            }else{
                canvas = new OffscreenCanvas(width, height);
            }
        }
        if (canvas.width != width) {
            canvas.width = width;
        }
        if (canvas.height != height) {
            canvas.height = height;
        }
        return canvas;
    };
})();

const getCanvas = (() => {
    // Offscreen canvas used to create encoded image from cv.Mat.
    // Reuse it if possible.
    let canvas: any = null;

    /**
     * @param {Number} width
     * @param {Number} height
     * @returns {OffscreenCanvas | HTMLCanvasElement}
     */
    return (width: number, height:number): any => {
        if (!canvas) {
            if(typeof OffscreenCanvas == 'undefined'){
                canvas = document.createElement("canvas");
                canvas.height = ''+height+'px';
                canvas.width = ''+width+'px';
            }else{
                canvas = new OffscreenCanvas(width, height);
            }
        }
        if (canvas.width != width) {
            canvas.width = width;
        }
        if (canvas.height != height) {
            canvas.height = height;
        }
        return canvas;
    };
})();

/**
 * Convert ImageData to image URL.
 *
 * @param {ImageData} imageData image.
 */
export async function imageDataToDataUrl(imageData: ImageData) {
    if (!imageData) {
        return "";
    }

    const canvas: any = getCanvas(imageData.width, imageData.height);

    // image based context might be faster, but its not working good in firefox, r and b are swapped...
    /*const imageBitmap = await createImageBitmap(imageData);
    const ctx = canvas.getContext('bitmaprenderer');
    ctx.transferFromImageBitmap(imageBitmap);//*/

    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    //ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.putImageData(imageData, 0, 0);//*/

    const imgType = "image/png";
    if (canvas instanceof HTMLCanvasElement) {
        return canvas.toDataURL(imgType)
    }
    if (canvas instanceof OffscreenCanvas) {
        // @ts-ignore
        return URL.createObjectURL(await canvas.convertToBlob({type: imgType}));
    }
    console.warn("Unknown canvas type:", canvas);
    return "";
}

/**
 * String containing integer RGBA values separated by spaces
 * e.g. "0 18 120 255".
 * @typedef {String} RGBA
 */

/**
 * Mapping of dBZ to RGBA.
 * @typedef {Object<Number, RGBA>} Colormap
 */

/**
 * Set of colortables for different weather conditions.
 * Mat must be 1-column, 256-row, 4-channel, unsigned 8-bit.
 * @typedef {{
 * 	rain: Mat,
 *	frain: Mat,
 *	mix: Mat,
 *	snow: Mat
 * }} ColortablesMap
 */

/**
 * Convert colortable map to cv.Mat.
 * @param {Colormap} colorsObj Colortable JSON.
 * @param minDbz
 * @returns {cv.Mat} Colortable image.
 */
function colortableToMat(colorsObj: any, minDbz: number = 0) {
    // Initialize to 0 to make all invalid dBZ values transparent by default.
    const mat = Mat.createFromSize(1, 256);
    if (!mat) {
        console.warn("Failed to create Mat for colortable!");
        return null;
    }
    const channels = mat.channels();

    for (const dbzName of Object.getOwnPropertyNames(colorsObj)) {

		//Because back-end already sets default minDbz to 20, visually we need to display 20 but subtract 20 for the final calculation
		//Temporary starting from 0
        if (parseInt(dbzName) < minDbz)
            continue;
        
        const colors = colorsObj[dbzName].split(" ");
        if (colors.length < 4)
            continue;

        // Nexrad files store dBZ values as signed type to allow for negative dBZ.
        // We don't really need negative dBZ and image data is typically stored as unsigned type.
        // Values are shifted up by 127 during conversion from signed to unsigned
        // to fit into unsigned range without losing data.
        // We can skip this to keep pixel values exactly corresponding to dBZ values,
        // but it will truncate negative dBZ and will make it harder to
        // visually debug raw tiles because they will be very dim (e.g. 20 dBZ out of 255).
        // Instead, simply shift colortable up by 127 as well to match dBZ tiles.
        const dbz = parseInt(dbzName) + 127;

        // Each image row corresponds to dBZ value.
        let pixel = mat.data();
        for (let i = 0, count = Math.min(channels, colors.length); i < count; ++i) {
            pixel[dbz * channels + i] = parseInt(colors[i]);
        }
    }
    return mat;
}


/**
 * Load colortables and finish module initialization.
 * @param {Object} module
 * @param {String} rainUrl
 * @param {String} frainUrl
 * @param {String} mixUrl
 * @param {String} snowUrl
 * @param {String} emptyUrl
 * @param minDbz
 */
export async function initColortables(module:any, rainUrl: any, frainUrl: any, mixUrl: any, snowUrl: any, emptyUrl: any, minDbz: number = 0) {
    module.colorTables.rain = colortableToMat(rainUrl, minDbz);
    module.colorTables.frain = colortableToMat(frainUrl, minDbz);
    module.colorTables.mix = colortableToMat(mixUrl, minDbz);
    module.colorTables.snow = colortableToMat(snowUrl, minDbz);
    module.colorTables.empty = colortableToMat(emptyUrl, minDbz);
    await module.finalInit();
}

/**
 * Replace dBZ values with colors.
 * @param {Mat} dbzMat Image with dBZ values.
 * @param colortables
 * @returns {Mat | null} Colored image on success, null on error.
 */
function putColors(dbzMat: any, colortables: any) {
    // Validate input parameters.
    if (!dbzMat || !colortables || dbzMat.empty())
        return null;

    // Validate colortables.
    for (const name of ["rain", "frain", "mix", "snow", "empty"]) {
        if (!colortables[name]) {
            console.warn("Missing colortable:", name);
            return null;
        }
        const mat = colortables[name];
        if (!Mat.validType(mat)) {
            console.warn("Colortable", name, "is not a Mat!");
            return null;
        }
        if (mat.channels() != 4) {
            console.warn("Colortable", name, "has unsupported number of channels:", mat.channels());
            return null;
        }
    }

    // Input image should contain dBZ, HRRR, and MRMS values.
    if (dbzMat.channels() < 3) {
        console.warn("Unsupported number of channels in dBZ image:", dbzMat.channels());
        return null;
    }

    // Prepare data arrays for repeated access.
    let dbzArray = dbzMat.data();
    const rainArray = colortables.rain.data();
    const frainArray = colortables.frain.data();
    const mixArray = colortables.mix.data();
    const snowArray = colortables.snow.data();
    const emptyArray = colortables.empty.data();
    if (!dbzArray || !rainArray || !frainArray || !mixArray || !snowArray) {
        console.warn("Failed to get images data arrays!");
        return null;
    }

    // HRRR values for weather types. See DbzToImage app of radar backend.
    const RAIN = 1 << 0;
    const FRAIN = 1 << 1;
    const ICE = 1 << 2;
    const SNOW = 1 << 3;

    //
    var rainChannels = colortables.rain.channels();
    var dbzChannels = dbzMat.channels();
    var dbzIdxMax = dbzMat.width() * dbzMat.height() * dbzChannels;
    // For each pixel in final image: determine appropriate colortable and write color values.
    for(let dbzIdx = 0; dbzIdx < dbzIdxMax; dbzIdx += dbzChannels) {
        /**
		 * Old logic with 3 channels mrms/hrrr/dbz
		const mrms = dbzArray[dbzIdx + 0];
        const hrrr = dbzArray[dbzIdx + 1];
        const dbz = dbzArray[dbzIdx + 2];

        let colortable = null;
        // Snow has the top priority.
        if(mrms || (hrrr & SNOW)) {
            colortable = snowArray;
        }
        // Ice Pellets or Ice Pellets and Rain or Ice Pellets and Freezing Rain -> Mix.
        else if(hrrr & ICE) {
            colortable = mixArray;
        }
        // If FRain or FRain+Rain then use FRain.
        else if((hrrr == FRAIN) || (hrrr == (RAIN | FRAIN))) {
            colortable = frainArray;
        }
        // Rain for anything else.
        else {
            colortable = rainArray;
        }*/
		
		//New logic with two channels dbz/cls
		const dbz = dbzArray[dbzIdx + 2];
		const cls = dbzArray[dbzIdx + 1];
		
		let colortable = null;
        // Snow has the top priority.
        if(cls == SNOW) {
            colortable = snowArray;
        }
        // Ice Pellets or Ice Pellets and Rain or Ice Pellets and Freezing Rain -> Mix.
        else if(cls == ICE) {
            colortable = mixArray;
        }
        // If FRain or FRain+Rain then use FRain.
        else if(cls == FRAIN) {
            colortable = frainArray;
        }
        // Rain for anything else.
        else if(cls == RAIN){
            colortable = rainArray;
        }
		// Apply empty colortable if no cls
        else {
			colortable = emptyArray;
        }
		
        // All colortables should be the same size so we can use any of them to determine 1D index.
        const colorIdx = dbz * rainChannels;
        dbzArray[dbzIdx + 0] = colortable[colorIdx + 0];
        dbzArray[dbzIdx + 1] = colortable[colorIdx + 1];
        dbzArray[dbzIdx + 2] = colortable[colorIdx + 2];
        dbzArray[dbzIdx + 3] = colortable[colorIdx + 3];
    }

    return dbzMat;
}

/**
 * Get dBZ data from image element, replace with colors, and paint back on the element.
 * @param url
 * @param {HTMLImageElement} imgElement
 * @param {ColortablesMap} colortables
 * @param loadFunction
 * @returns {Promise<boolean>} True if swap is successful, false otherwise.
 */
async function swapColor(url: string | null, imgElement: HTMLImageElement, colortables: any, loadFunction=null) {
    let dbzMat = null;
    let colorMat = null;
    var result = false;
    try {
        if(url)
            dbzMat = await Mat.createFromUrl(url);
        else
            dbzMat = await Mat.createFromImg(imgElement);

        colorMat = putColors(dbzMat, colortables);
        if (colorMat) {
            try {
                if (/*!url && */loadFunction) { // to prevent black image blinking in FF 
                    imgElement.onload = loadFunction;
                    imgElement.onerror = loadFunction;
                    imgElement.onabort = loadFunction;
                }

                imgElement.decoding = "sync"; // fix whatever blinking in FF, todo: check if it affects performance in other browsers
                imgElement.src = await colorMat.toDataUrl();
                /*if (url && loadFunction) { // to prevent black image blinking in FF 
                    imgElement.decode().then(() => loadFunction()).catch((encodingError) => loadFunction());
                }*/

                result = true;
            } catch (e) {
                console.warn("Failed to create DataUrl:", e)
            }
        }
    } catch (e) {
        console.warn("Failed to swap dBZ with color:", e);
    } finally {
        if (dbzMat) {
            dbzMat.delete();
        }
        if (colorMat && (colorMat != dbzMat)) {
            colorMat.delete();
        }
    }
    return result;
}

export function createDbzLoadHandler(module: any, loadFunction: any=null, updateFromUrl=false) {
    // Capture handler in its own closure.
    let handler: any = null;

    if (updateFromUrl) {
        handler = async (overlay: any, scrUrl: string) => {
            var swapSuccess = false;
            if (overlay && scrUrl) {
                var srcElement = overlay.getElement();
                if (srcElement) {
                    swapSuccess = await swapColor(scrUrl, srcElement, module.colorTables, loadFunction)
                    overlay.dbzColored = swapSuccess;
                    if (swapSuccess && !loadFunction) {
                        srcElement.hidden = false;
                        srcElement.classList.add('loaded-layer');
                        overlay.setOpacity(module.tileOpacity);
                    }
                }
            }

            if (!swapSuccess && loadFunction)
                loadFunction();
        };
    } else {
        handler = async (event : any) => {
            var swapSuccess = false;
            if (event && event.srcElement) {
                event.target.off('load', handler);
                swapSuccess = await swapColor(null, event.srcElement, module.colorTables, loadFunction)
                event.target.dbzColored = swapSuccess;
                if (swapSuccess && !loadFunction) {
                    event.srcElement.hidden = false;
                    event.target.setOpacity(module.tileOpacity);
                    event.srcElement.classList.add('loaded-layer');
                    
                }
            }

            if (!swapSuccess && loadFunction)
                loadFunction();
        };
    }
    return handler;
}

